import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Heart, Activity, User, FileText, Brain, Send, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/hooks/use-toast'

import type { Medication } from '@/types/medicamento'

import { Etapa1Identificacao } from '@/components/anamnese/Etapa1Identificacao'
import { Etapa2Responsavel } from '@/components/anamnese/Etapa2Responsavel'
import { Etapa3HistoricoClinico } from '@/components/anamnese/Etapa3HistoricoClinico'
import { Etapa4Medicamentos } from '@/components/anamnese/Etapa4Medicamentos'
import { Etapa5CapacidadeFuncional } from '@/components/anamnese/Etapa5CapacidadeFuncional'
import { Etapa6CognicaoComportamento } from '@/components/anamnese/Etapa6CognicaoComportamento'
import { Etapa7MobilidadeRisco } from '@/components/anamnese/Etapa7MobilidadeRisco'
import { Etapa8Nutricao } from '@/components/anamnese/Etapa8Nutricao'
import { Etapa9Sensorial } from '@/components/anamnese/Etapa9Sensorial'
import { Etapa10Finalizacao } from '@/components/anamnese/Etapa10Finalizacao'

interface AnamneseData {
  // Etapa 1 - Identificação
  nome_completo: string
  data_nascimento: string
  cpf: string
  rg: string
  convenio: string
  // Etapa 2 - Responsável
  responsavel: {
    nome: string
    parentesco: string
    telefone: string
    whatsapp: string
    email: string
    responsavel_financeiro: boolean
    existe_curatela: boolean
    existe_procuracao: boolean
    endereco?: {
      cep: string
      rua: string
      numero: string
      complemento: string
      bairro: string
      cidade: string
      estado: string
    }
  }
  
  // Etapa 3 - Histórico Clínico
  comorbidades: Array<{
    categoria: string
    condicao: string
    possui: boolean
    detalhes?: string
  }>
  
  // Etapa 4 - Medicamentos
  medicamentos: Medication[]
  
  // Etapa 5 - Capacidade Funcional
  funcionalidade: Array<{
    atividade: string
    nivel_dependencia: 'independente' | 'supervisao' | 'ajuda_parcial' | 'dependencia_total'
    observacoes?: string
  }>
  
  // Etapa 6 - Cognição e Comportamento
  cognicao: {
    reconhece_familiares: boolean
    esquece_eventos: boolean
    agressividade: boolean
    agitacao: boolean
    fuga: boolean
    alucinacoes: boolean
    demencia: boolean
    alzheimer: boolean
  }
  
  // Etapa 7 - Mobilidade e Risco
  mobilidade: {
    usa_bengala: boolean
    usa_andador: boolean
    cadeira_rodas: boolean
    acamada: boolean
    claudica: boolean
    quedas: boolean
    oxigenio: boolean
    feridas: boolean
    broncoaspiracao: boolean
  }
  
  // Etapa 8 - Nutrição
  nutricao: {
    peso: string
    altura: string
    dieta: string
    engasgos: boolean
    sonda: boolean
    perda_peso: boolean
  }
  
  // Etapa 9 - Capacidades Sensoriais
  sensorial: {
    // Visão
    usa_oculos?: boolean
    condicoes_visuais?: boolean
    sensibilidade_luz?: boolean
    visao_noturna?: boolean
    nenhuma_opcao_visao?: boolean
    
    // Audição
    usa_aparelho_auditivo?: boolean
    dificuldade_ouvir?: boolean
    zumbidos?: boolean
    nenhuma_opcao_auditiva?: boolean
    
    // Paladar e Olfato
    usa_protese_dentaria?: boolean
    alteracoes_paladar?: boolean
    perda_olfato?: boolean
    nenhuma_opcao_paladar_olfato?: boolean
    
    // Sensibilidade à Dor
    dor_cronica?: boolean
    baixa_sensibilidade_dor?: boolean
    nenhuma_opcao_dor?: boolean
    
    // Equilíbrio e Tontura
    tonturas_frequentes?: boolean
    sensibilidade_desequilibrio?: boolean
    vertigens?: boolean
    quedas_desequilibrio?: boolean
    nenhuma_opcao_equilibrio?: boolean
    
    // Fala e Deglutição
    dificuldades_fala_degluticao?: boolean
    nenhuma_opcao_fala?: boolean
  }
  
  // Etapa 10 - Finalização
  lgpd_accept: boolean
  declaration_accept: boolean
  signature: string
  veracidade_accept: boolean
  veracidade_etapa3: boolean
  veracidade_etapa4: boolean
  veracidade_etapa6: boolean
  veracidade_etapa7: boolean
  veracidade_etapa8: boolean
  veracidade_etapa9: boolean
}

const ETAPAS = [
  { id: 1, titulo: 'Identificação', descricao: 'Dados do idoso(a)', icon: User },
  { id: 2, titulo: 'Responsável', descricao: 'Informações do familiar', icon: Heart },
  { id: 3, titulo: 'Histórico Clínico', descricao: 'Condições de saúde', icon: Activity },
  { id: 4, titulo: 'Medicamentos', descricao: 'Tratamentos em uso', icon: FileText },
  { id: 5, titulo: 'Capacidade Funcional', descricao: 'Avaliação funcional', icon: Activity },
  { id: 6, titulo: 'Cognição', descricao: 'Aspectos cognitivos', icon: FileText },
  { id: 7, titulo: 'Mobilidade', descricao: 'Riscos mobilidade', icon: User },
  { id: 8, titulo: 'Nutrição', descricao: 'Dados nutricionais', icon: FileText },
  { id: 9, titulo: 'Capacidades Sensoriais', descricao: 'Avaliação sensorial', icon: Brain },
  { id: 10, titulo: 'Finalização', descricao: 'Revisão e envio', icon: Send }
]

export default function AnamnesePage() {
  const params = useParams()
  const token = params.token || ''
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [saving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [residentialName, setResidentialName] = useState<string>('')
  const [linkData, setLinkData] = useState<any>(null)
  const [formData, setFormData] = useState<AnamneseData>({
    // Etapa 1
    nome_completo: '',
    data_nascimento: '',
    cpf: '',
    rg: '',
    convenio: '',
    // Etapa 2
    responsavel: {
      nome: '',
      parentesco: '',
      telefone: '',
      whatsapp: '',
      email: '',
      responsavel_financeiro: true,
      existe_curatela: false,
      existe_procuracao: false,
      endereco: {
        cep: '',
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: ''
      }
    },
    
    // Etapa 3
    comorbidades: [],
    
    // Etapa 4
    medicamentos: [],
    
    // Etapa 5
    funcionalidade: [],
    
    // Etapa 6
    cognicao: {
      reconhece_familiares: false,
      esquece_eventos: false,
      agressividade: false,
      agitacao: false,
      fuga: false,
      alucinacoes: false,
      demencia: false,
      alzheimer: false
    },
    
    // Etapa 7
    mobilidade: {
      usa_bengala: false,
      usa_andador: false,
      cadeira_rodas: false,
      acamada: false,
      claudica: false,
      quedas: false,
      oxigenio: false,
      feridas: false,
      broncoaspiracao: false
    },
    
    // Etapa 8
    nutricao: {
      peso: '',
      altura: '',
      dieta: '',
      engasgos: false,
      sonda: false,
      perda_peso: false
    },
    
    // Etapa 9
    sensorial: {},
    
    // Etapa 10
    lgpd_accept: false,
    declaration_accept: false,
    signature: '',
    veracidade_accept: false,
    veracidade_etapa3: false,
    veracidade_etapa4: false,
    veracidade_etapa6: false,
    veracidade_etapa7: false,
    veracidade_etapa8: false,
    veracidade_etapa9: false
  })

  useEffect(() => {
    // Rolar para o topo sempre que mudar de etapa
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/anamnese-get/${token}?_t=${Date.now()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            }
          }
        )

        const result = await response.json()

        if (!response.ok) {
          if (response.status === 410 || result.status === 'completed') {
            const reason = result.reason || 'completed'
            if (reason === 'expired') {
              navigate('/anamnese/expirado')
            } else if (reason === 'cancelled') {
              navigate('/anamnese/cancelado')
            } else {
              navigate('/anamnese/completo')
            }
            return
          }

          toast({
            title: "Erro",
            description: result.error || 'Não foi possível carregar o formulário',
            variant: "destructive"
          })
          setLoading(false)
          return
        }

        // Carregar dados do link e residencial
        if (result.success && result.data) {
          setLinkData(result.data.link)
          setResidentialName(result.data.link.residencial_name || 'Residencial')
          
          // Se houver dados salvos da anamnese, restaurar
          if (result.data.anamnese?.patient_data) {
            const savedData = result.data.anamnese.patient_data
            setFormData(prev => ({
              ...prev,
              ...savedData,
              responsavel: savedData.responsavel || prev.responsavel,
              comorbidades: savedData.comorbidades || prev.comorbidades,
              medicamentos: savedData.medicamentos || prev.medicamentos,
              funcionalidade: savedData.funcionalidade || prev.funcionalidade,
              cognicao: savedData.cognicao || prev.cognicao,
              mobilidade: savedData.mobilidade || prev.mobilidade,
              nutricao: savedData.nutricao || prev.nutricao,
              sensorial: savedData.sensorial || prev.sensorial
            }))
            setCurrentStep(result.data.anamnese.current_step || 1)
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        toast({
          title: "Erro",
          description: 'Falha ao conectar com o servidor',
          variant: "destructive"
        })
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      loadInitialData()
    }
  }, [token, navigate])

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => {
      const parts = field.split('.')
      if (parts.length === 2) {
        const [parent, child] = parts
        return {
          ...prev,
          [parent]: {
            ...(typeof prev[parent as keyof AnamneseData] === 'object' && prev[parent as keyof AnamneseData] != null ? prev[parent as keyof AnamneseData] as any : {}),
            [child]: value
          }
        }
      }
      if (parts.length === 3) {
        const [parent, child, grandchild] = parts
        const parentObj = (typeof prev[parent as keyof AnamneseData] === 'object' && prev[parent as keyof AnamneseData] != null ? prev[parent as keyof AnamneseData] as any : {})
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: {
              ...(typeof parentObj[child] === 'object' && parentObj[child] != null ? parentObj[child] : {}),
              [grandchild]: value
            }
          }
        }
      }
      return {
        ...prev,
        [field]: value
      }
    })
  }, [])

  const isStepValid = (stepId: number): boolean => {
    switch (stepId) {
      case 1:
        return !!(
          formData.nome_completo && 
          formData.data_nascimento && 
          formData.cpf
        )
      case 2:
        return !!(
          formData.responsavel?.nome?.trim() &&
          formData.responsavel?.responsavel_financeiro !== undefined &&
          formData.responsavel?.parentesco?.trim() &&
          formData.responsavel?.whatsapp?.trim() &&
          formData.responsavel?.email?.trim() &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.responsavel?.email || '') &&
          formData.responsavel?.endereco?.cep &&
          formData.responsavel?.endereco?.rua &&
          formData.responsavel?.endereco?.numero &&
          formData.responsavel?.endereco?.bairro &&
          formData.responsavel?.endereco?.cidade &&
          formData.responsavel?.endereco?.estado
        )
      case 3:
        return !!formData.veracidade_etapa3 && !!(formData.comorbidades && formData.comorbidades.length > 0)
      case 4:
        return !!formData.veracidade_etapa4
      case 5:
        return !!formData.veracidade_accept && formData.funcionalidade?.length >= 11
      case 6:
        return !!formData.veracidade_etapa6
      case 7:
        return !!formData.veracidade_etapa7
      case 8:
        return !!formData.veracidade_etapa8 && !!(formData.nutricao?.peso && formData.nutricao?.altura && formData.nutricao?.dieta)
      case 9:
        return !!formData.veracidade_etapa9
      case 10:
        return !!(formData.lgpd_accept && formData.declaration_accept && formData.signature)
      default:
        return false
    }
  }

  const canNavigateToStep = (targetStepId: number): boolean => {
    if (targetStepId <= currentStep) return true
    
    // Para ir para uma etapa futura, todas as anteriores a ela devem estar válidas
    for (let i = 1; i < targetStepId; i++) {
      if (!isStepValid(i)) return false
    }
    return true
  }

  const handleNextStep = () => {
    if (currentStep < 10) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }


  const handleSubmit = async () => {
    if (!linkData?.id) {
      toast({
        title: "Erro",
        description: 'Dados do formulário não encontrados',
        variant: "destructive"
      })
      return
    }

    setSubmitting(true)
    toast({
      title: "Enviando...",
      description: "Seu formulário está sendo enviado."
    })
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/anamnese-submit/${token}?_t=${Date.now()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            final_patient_data: formData,
            medicamentos: formData.medicamentos,
            comorbidades: formData.comorbidades,
            funcionalidade: formData.funcionalidade,
            lgpd_accept: formData.lgpd_accept,
            declaration_accept: formData.declaration_accept,
            signature: formData.signature
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao enviar formulário')
      }

      toast({
        title: "Enviado!",
        description: result.message || "Formulário enviado com sucesso."
      })
      
      // Redirecionar para página de sucesso
      navigate('/anamnese/sucesso')
      
    } catch (error: any) {
      console.error('Erro no submit:', error)
      toast({
        title: "Erro",
        description: error.message || 'Falha ao enviar o formulário',
        variant: "destructive"
      })
    } finally {
      setSubmitting(false)
    }
  }

  const renderEtapa = () => {
    switch (currentStep) {
      case 1:
        return (
          <Etapa1Identificacao
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 2:
        return (
          <Etapa2Responsavel
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
          />
        )
      
      case 3:
        return (
          <Etapa3HistoricoClinico
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 4:
        return (
          <Etapa4Medicamentos
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 5:
        return (
          <Etapa5CapacidadeFuncional
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 6:
        return (
          <Etapa6CognicaoComportamento
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 7:
        return (
          <Etapa7MobilidadeRisco
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 8:
        return (
          <Etapa8Nutricao
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 9:
        return (
          <Etapa9Sensorial
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      
      case 10:
        return (
          <Etapa10Finalizacao
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit}
            saving={saving}
            submitting={submitting}
          />
        )
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Carregando formulário...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F6F9F7'}}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Heart className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Anamnese de Triagem</h1>
              <p className="text-gray-600">{String(residentialName)}</p>
              <p className="text-xs text-gray-400">Token: {String(token)}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progresso */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Etapa {String(currentStep)} de 10
            </h2>
            <span className="text-sm text-gray-500">
              {String(Math.round((currentStep / 10) * 100))}% concluído
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 10) * 100}%` }}
            ></div>
          </div>
          
          {/* Indicadores de etapas com ícones (Botões de Navegação Direta) */}
          <div className="flex justify-between overflow-x-auto">
            {ETAPAS.map((etapa) => {
              const Icon = etapa.icon
              const isCompleted = etapa.id < currentStep
              const isCurrent = etapa.id === currentStep
              const isAllowed = canNavigateToStep(etapa.id)
              
              return (
                <button
                  key={etapa.id}
                  onClick={() => isAllowed && setCurrentStep(etapa.id)}
                  disabled={!isAllowed}
                  className={`flex flex-col items-center min-w-fit transition-all ${
                    isAllowed ? 'hover:opacity-80 active:scale-95 cursor-pointer' : 'opacity-40 cursor-not-allowed'
                  } ${
                    isCompleted ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-gray-400'
                  }`}
                  title={isAllowed ? `Ir para Etapa ${String(etapa.id)}` : `Etapa ${String(etapa.id)} bloqueada`}
                >
                  <div className={`p-1.5 rounded-full transition-colors ${
                    isCompleted ? 'bg-green-100' : isCurrent ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    {Icon && <Icon className="h-3 w-3 sm:h-4 sm:w-4" />}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block font-medium">{String(etapa.id)}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Conteúdo da Etapa */}
      <div className="max-w-4xl mx-auto px-4 pb-24">
        {renderEtapa()}
      </div>
    </div>
  )
}
